import { useEffect, useState } from 'react';
import { GoMotoComponent, GoMotoFunction } from '../../../../API/GoMoto.types';
import { TransportationClosedProps } from './TransportationClosed.types';
import { ITransport, useCart } from '../../../../context/cart';
import { TransportType } from '../../../../API/Enums';
import { getService, IService, LocalizedTransportationOption } from '../../../../API/GoMoto';
import { useKioskState } from '../../../../context/Kiosk/kiosk';
import { Spinner } from '../../../../controls/Spinner/Spinner';
import { PickUpTime } from '../PickUpTime/PickUpTime';
import clone from 'just-clone';

/**
 * This component handles whether we should skip transportation or let the customer enter when
 * they will return to pick up their vehicle.
 *
 * @param onProceed - {@link TransportationClosedProps.onProceed onProceed} description.
 * @param onSkip - {@link TransportationClosedProps.onSkip onSkip} description.
 * @param transportOpts - {@link TransportationClosedProps.transportOpts transportOpts} description.
 * @param pickUpTimeEnabled - {@link TransportationClosedProps.pickUpTimeEnabled pickUpTimeEnabled} description.
 */
export const TransportationClosed: GoMotoComponent<TransportationClosedProps> = ({
    onProceed,
    onSkip,
    transportOpts,
    pickUpTimeEnabled,
}) => {
    const { transportation } = useCart();
    const { key } = useKioskState();
    const [transportOpt, setTransportOpt] = useState<LocalizedTransportationOption | undefined>(undefined);
    const [transport, setTransport] = useState<ITransport>(transportation);
    const [loading, setLoading] = useState<boolean>(false);
    const [shouldSkip, setShouldSkip] = useState<boolean>(false);

    useEffect(() => {
        const getTransportOptService: GoMotoFunction<[string], Promise<IService | undefined>> = async (opCode) => {
            setLoading(true);
            const service: IService | undefined = opCode ? await getService(key, opCode) : undefined;
            setLoading(false);

            return service;
        };

        const generateTransportOpts: GoMotoFunction<[void], Promise<void>> = async () => {
            const selected: LocalizedTransportationOption | undefined = transportOpts!.find((transportOpt: LocalizedTransportationOption) => {
                return transportOpt.type === TransportType.drop;
            });

            setTransportOpt(selected);

            if (selected) {
                const newTransport: ITransport = clone(transport);
                const service: IService | undefined = await getTransportOptService(selected.opCode);

                newTransport.type = selected.type;
                newTransport.subtype = selected.subtype;
                newTransport.transportService = service;
                newTransport.dmsTransportationType = selected.dmsTransportationType;
                newTransport.opCode = selected.opCode;

                if (!selected.showPickupTime || !pickUpTimeEnabled) {
                    newTransport.pickUpTime = undefined;
                    setTransport(newTransport);
                    setShouldSkip(true);
                }
                else {
                    setTransport(newTransport);
                }
            }
        };

        if (!transportOpt && transportOpts) {
            generateTransportOpts();
        }
    }, [transportation, transportOpt, transportOpts, transport, pickUpTimeEnabled, key]);

    useEffect(() => {
        if (shouldSkip) {
            onSkip({ ...transport,
                pickUpTime: undefined });
        }
    }, [onSkip, shouldSkip, transport]);

    if (shouldSkip || !transportOpt || !transport || loading) {
        return (
            <Spinner />
        );
    }
    else if (transportOpt.showPickupTime && pickUpTimeEnabled) {
        return (
            <PickUpTime
                onProceed={({ pickUpTime }) => {
                    onProceed({ ...transport,
                        pickUpTime });
                }}
            />
        );
    }
    else {
        return null;
    }
};